import React from "react";
import { useNavigate } from "react-router";
import { DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import MentionInput from "@common/components/mentionInput/MentionInput.tsx";
import { EntityType } from "@common/Common.types.ts";
import { ValidationError } from "@common/validationError.ts";
import { exhaustiveGuard } from "@common/utilities.ts";
import { isNullOrWhitespace } from "@common/stringOperations.ts";
import { TagRelationType } from "@common/components/tagsMultiSelect/TagsMultiSelect.types.ts";
import TagsMultiSelectStandAlone from "@common/components/tagsMultiSelect/tagsMultiSelectAutoUpdater/TagsMultiSelectAutoUpdater.tsx";
import DocumentsEmptyState from "@common/components/documentsEmptyState/DocumentsEmptyState.tsx";
import { useTranslation } from "@localization/useTranslation.ts";
import { useUrlProvider } from "../useUrlProvider.ts";
import { EntityTemplateField, isProcessStandardField, ProcessStandardField, TemplateEntityType } from "./EntityTemplate.types.ts";
import { InputFieldTypeEnum } from "./inputFields/InputFields.types.ts";
import { CustomFieldValue } from "../system/systemEdit/SystemEdit.types.ts";
import { ProcessStatus } from "../process/Process.types.ts";
import ProcessDocuments from "../process/processOverview/documents/ProcessDocuments.tsx";
import ProcessStatusField from "./fields/processFields/ProcessStatusField.tsx";
import ProcessExcludedUsersField from "./fields/processFields/ProcessExcludedUsersField.tsx";
import ProcessAssetsField from "./fields/processFields/ProcessAssetsField.tsx";
import ProcessLegalEntitiesField from "./fields/processFields/ProcessLegalEntitiesField.tsx";
import ProcessProcessingActivitiesField from "./fields/processFields/ProcessProcessingActivitiesField.tsx";
import ComplianceAreasField from "./fields/ComplianceAreasField.tsx";
import AdditionalUserAccessField from "./fields/AdditionalUserAccessField.tsx";
import AccessAwareResponsibleField from "./fields/AccessAwareResponsibleField.tsx";
import VisibleToAllUsersField from "./fields/VisibleToAllUsersField.tsx";
import BusinessAreaAccessField from "./fields/BusinessAreaAccessField.tsx";
import BusinessAreaField from "./fields/BusinessAreaField.tsx";
import GroupCompaniesAccessField from "./fields/GroupCompaniesAccessField.tsx";
import { AccessControlEntity } from "./fields/useAccessResponsibilityHelper.hooks.ts";
import CustomTextField from "./fields/customFields/CustomTextField.tsx";
import CustomRichTextField from "./fields/customFields/CustomRichTextField.tsx";
import CustomCountryField from "./fields/customFields/CustomCountryField.tsx";
import CustomSingleSelectField from "./fields/customFields/CustomSingleSelectField.tsx";
import CustomMultiSelectField from "./fields/customFields/CustomMultiSelectField.tsx";
import CustomDateField from "./fields/customFields/CustomDateField.tsx";
import CustomNumberField from "./fields/customFields/CustomNumberField.tsx";
import CustomAmountField from "./fields/customFields/CustomAmountField.tsx";
import CustomCheckbox from "./fields/customFields/CustomCheckbox.tsx";
import CustomLegalEntityField from "./fields/customFields/CustomLegalEntityField.tsx";
import CustomUserField from "./fields/customFields/CustomUserField.tsx";
import CustomCalculatedField from "./fields/customFields/CustomCalculatedField.tsx";
import CustomDatePeriodField from "./fields/customFields/CustomDatePeriodField.tsx";
import { ComputedFieldTemplateEditing } from "./fields/customFields/computedField.types.ts";

export interface ProcessFieldModel extends AccessControlEntity {
    id: string;
    name?: string;
    description?: string;
    note?: string;
    status?: ProcessStatus;
    businessAreaIds: Array<string>;
    complianceAreaIds: Array<string>;
    assetIds: Array<string>;
    legalEntityIds: Array<string>;
    processingActivityIds: Array<string>;
    entityTemplateId?: string;
    customFieldValues: Array<CustomFieldValue>;
}

const processCustomFieldApiPath = "processes";

// Mirrors the backend, where becoming responsible lifts an exclusion, so the excluded users field stops showing them
// without waiting for a refetch.
function liftExclusionOfResponsible(process: ProcessFieldModel) {
    process.excludedUsers = process.excludedUsers.filter((userId) => userId !== process.responsibleId);
}

export interface ProcessStandardFieldProps {
    field: EntityTemplateField;
    isLoading: boolean;
    process?: ProcessFieldModel;
    processId: string;
    onChange: (changeFunc: (process: ProcessFieldModel) => void) => Promise<void>;
    validationErrors: Array<ValidationError>;
    isPreview: boolean;
    disableAccessWarning?: boolean;
}

export interface ProcessCustomFieldProps {
    processId: string;
    process?: ProcessFieldModel;
    field: EntityTemplateField;
    onChange: (changeFunc: (process: ProcessFieldModel) => void) => void;
    isPreview: boolean;
    isLoading: boolean;
    validationErrors: Array<ValidationError>;
    computedFieldEditing?: ComputedFieldTemplateEditing; // For template editor preview
}

export interface ProcessFieldRendererProps {
    field: EntityTemplateField;
    process?: ProcessFieldModel;
    isLoading: boolean;
    processId?: string;
    onChange: (changeFunc: (process: ProcessFieldModel) => void) => Promise<void>;
    validationErrors?: Array<ValidationError>;
    onCustomFieldChange: (changeFunc: (process: ProcessFieldModel) => void) => void;
    isPreview: boolean;
    computedFieldEditing?: ComputedFieldTemplateEditing; // For template editor preview
}

export const ProcessStandardFieldRenderer: React.FC<ProcessStandardFieldProps> = ({
    field,
    process,
    processId,
    onChange,
    validationErrors,
    isLoading,
    isPreview,
    disableAccessWarning,
}) => {
    const { translateString } = useTranslation();
    const navigate = useNavigate();
    const urlProvider = useUrlProvider();
    if (!field.standardFieldIdentifier || !isProcessStandardField(field.standardFieldIdentifier)) {
        return null;
    }

    const isDisabled = field.disabled || isPreview;
    const getErrorText = (standardField: ProcessStandardField) => validationErrors.find((v) => v.field === ProcessStandardField[standardField])?.error;
    // Mentions are saved as relations against the process id, so they cannot be made before the process exists.
    const mentionProps = isNullOrWhitespace(processId)
        ? { disableMentions: true as const }
        : { mentionSource: { entityFromId: processId, entityFromType: EntityType.Process } };
    const navigateToProcesses = () => navigate(urlProvider.getProcessesUrl());

    switch (field.standardFieldIdentifier) {
        case ProcessStandardField.ProcessName:
            return (
                <DotLegalTextField
                    label={translateString("name")}
                    value={process?.name ?? ""}
                    debounce
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.name = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(ProcessStandardField.ProcessName)}
                />
            );
        case ProcessStandardField.ProcessDescription:
            return (
                <MentionInput
                    multiline
                    rows={6}
                    label={translateString("description")}
                    value={process?.description ?? ""}
                    debounce
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.description = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(ProcessStandardField.ProcessDescription)}
                    {...mentionProps}
                />
            );
        case ProcessStandardField.ProcessNote:
            return (
                <MentionInput
                    multiline
                    rows={6}
                    label={translateString("note")}
                    value={process?.note ?? ""}
                    debounce
                    onChange={(value) =>
                        onChange((entity) => {
                            entity.note = value;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(ProcessStandardField.ProcessNote)}
                    {...mentionProps}
                />
            );
        case ProcessStandardField.ProcessStatus:
            return (
                <ProcessStatusField
                    status={process?.status}
                    onChange={(status) =>
                        onChange((entity) => {
                            entity.status = status;
                        })
                    }
                    disabled={isDisabled}
                    errorText={getErrorText(ProcessStandardField.ProcessStatus)}
                />
            );
        case ProcessStandardField.ProcessBusinessAreas:
            return (
                <BusinessAreaField
                    businessAreas={process?.businessAreaIds ?? []}
                    isLoading={isLoading}
                    onChange={(businessAreaIds) =>
                        onChange((entity) => {
                            entity.businessAreaIds = businessAreaIds;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={getErrorText(ProcessStandardField.ProcessBusinessAreas)}
                />
            );
        case ProcessStandardField.ProcessComplianceAreas:
            return (
                <ComplianceAreasField
                    complianceAreaIds={process?.complianceAreaIds ?? []}
                    onChange={(complianceAreaIds) =>
                        onChange((entity) => {
                            entity.complianceAreaIds = complianceAreaIds;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={getErrorText(ProcessStandardField.ProcessComplianceAreas)}
                />
            );
        case ProcessStandardField.ProcessTags:
            return (
                <TagsMultiSelectStandAlone
                    entityId={processId}
                    isLoading={false}
                    entityType={TagRelationType.Process}
                    disabled={isDisabled}
                    errorText={getErrorText(ProcessStandardField.ProcessTags)}
                    disableQuery={isPreview}
                />
            );
        case ProcessStandardField.ProcessResponsible:
            return (
                <AccessAwareResponsibleField
                    entity={process}
                    isLoading={isLoading}
                    onChange={(changeFunc) =>
                        onChange((entity) => {
                            changeFunc(entity);
                            liftExclusionOfResponsible(entity);
                        })
                    }
                    onConfirmNavigate={navigateToProcesses}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    toolTipText={translateString("processResponsibleTooltip")}
                    warningText={isPreview ? undefined : translateString("processResponsibleWarning")}
                    errorText={getErrorText(ProcessStandardField.ProcessResponsible)}
                />
            );
        case ProcessStandardField.ProcessAdditionalUserAccess:
            return (
                <AdditionalUserAccessField
                    entity={process}
                    isLoading={isLoading}
                    onChange={onChange}
                    onConfirmNavigate={navigateToProcesses}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    toolTipText={translateString("processAdditionalAccessTooltip")}
                    warningText={translateString("processAdditionalAccessWarning")}
                    disableAccessWarning={disableAccessWarning}
                    errorText={getErrorText(ProcessStandardField.ProcessAdditionalUserAccess)}
                />
            );
        case ProcessStandardField.ProcessExcludedUsers:
            return (
                <ProcessExcludedUsersField
                    processId={processId}
                    process={process}
                    isLoading={isLoading}
                    onChange={onChange}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={getErrorText(ProcessStandardField.ProcessExcludedUsers)}
                />
            );
        case ProcessStandardField.ProcessVisibleToAllUsers:
            return (
                <VisibleToAllUsersField
                    entity={process}
                    onChange={onChange}
                    onConfirmNavigate={navigateToProcesses}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    toolTipText={translateString("visibleToAllUsersTooltip")}
                    confirmAccessRemovalKey="confirmProcessAccessRemovalBody"
                    disableAccessWarning={disableAccessWarning}
                    errorText={getErrorText(ProcessStandardField.ProcessVisibleToAllUsers)}
                />
            );
        case ProcessStandardField.ProcessBusinessAreaAccess:
            return (
                <BusinessAreaAccessField
                    entity={process}
                    isLoading={isLoading}
                    onChange={onChange}
                    onConfirmNavigate={navigateToProcesses}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    toolTipText={translateString("processBusinessAreaAccessTooltip")}
                    warningText={translateString("processBusinessAreaAccessWarning")}
                    disableAccessWarning={disableAccessWarning}
                    errorText={getErrorText(ProcessStandardField.ProcessBusinessAreaAccess)}
                />
            );
        case ProcessStandardField.ProcessGroupCompanies:
            return (
                <GroupCompaniesAccessField
                    entity={process}
                    isLoading={isLoading}
                    onChange={onChange}
                    onConfirmNavigate={navigateToProcesses}
                    addGroupEntityDialogHeader={translateString("addCompaniesToProcess")}
                    disabled={isDisabled}
                    isPreview={isPreview}
                    confirmAccessRemovalKey="confirmProcessAccessRemovalBody"
                    disableAccessWarning={disableAccessWarning}
                    errorText={getErrorText(ProcessStandardField.ProcessGroupCompanies)}
                />
            );
        case ProcessStandardField.ProcessAssets:
            return (
                <ProcessAssetsField
                    assetIds={process?.assetIds ?? []}
                    onChange={(assetIds) =>
                        onChange((entity) => {
                            entity.assetIds = assetIds;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={getErrorText(ProcessStandardField.ProcessAssets)}
                />
            );
        case ProcessStandardField.ProcessLegalEntities:
            return (
                <ProcessLegalEntitiesField
                    legalEntityIds={process?.legalEntityIds ?? []}
                    onChange={(legalEntityIds) =>
                        onChange((entity) => {
                            entity.legalEntityIds = legalEntityIds;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={getErrorText(ProcessStandardField.ProcessLegalEntities)}
                />
            );
        case ProcessStandardField.ProcessProcessingActivities:
            return (
                <ProcessProcessingActivitiesField
                    processingActivityIds={process?.processingActivityIds ?? []}
                    onChange={(processingActivityIds) =>
                        onChange((entity) => {
                            entity.processingActivityIds = processingActivityIds;
                        })
                    }
                    disabled={isDisabled}
                    isPreview={isPreview}
                    errorText={getErrorText(ProcessStandardField.ProcessProcessingActivities)}
                />
            );
        case ProcessStandardField.ProcessDocuments:
            // Documents are saved against the process id through their own endpoints, so they are not part of the model.
            if (isPreview) {
                return <DocumentsEmptyState />;
            }
            return (
                <ProcessDocuments
                    processId={processId}
                    processName={process?.name}
                    emptyStateWrappedInPaper
                    errorText={getErrorText(ProcessStandardField.ProcessDocuments)}
                />
            );
        default:
            return exhaustiveGuard(field.standardFieldIdentifier);
    }
};

export const ProcessCustomFieldRenderer: React.FC<ProcessCustomFieldProps> = ({
    process,
    processId,
    field,
    onChange,
    isPreview,
    isLoading = false,
    validationErrors,
    computedFieldEditing,
}) => {
    if (!field.customInputFieldType) {
        return null;
    }

    const isDisabled = isPreview || field.disabled;
    const value = (process?.customFieldValues ?? []).find((x) => x.customInputFieldId == field.customInputFieldId)?.value ?? "";
    const errorText = validationErrors.find((v) => v.field === field.id)?.error;

    switch (field.customInputFieldType) {
        case InputFieldTypeEnum.TextField:
            return (
                <CustomTextField
                    isLoading={isLoading}
                    entityId={processId}
                    entityApiPath={processCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.RichTextField:
            return (
                <CustomRichTextField
                    isLoading={isLoading}
                    entityId={processId}
                    entityApiPath={processCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.CountryField:
            return (
                <CustomCountryField
                    isLoading={isLoading}
                    entityId={processId}
                    entityApiPath={processCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.SingleDropDown:
            return (
                <CustomSingleSelectField
                    isLoading={isLoading}
                    entityId={processId}
                    entityApiPath={processCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.MultiDropDown:
            return (
                <CustomMultiSelectField
                    isLoading={isLoading}
                    entityId={processId}
                    entityApiPath={processCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    values={value as string[]}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.DateField:
            return (
                <CustomDateField
                    isLoading={isLoading}
                    entityId={processId}
                    entityApiPath={processCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as Date | null}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.NumberField:
            return (
                <CustomNumberField
                    isLoading={isLoading}
                    entityId={processId}
                    entityApiPath={processCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as string}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.AmountField:
            return (
                <CustomAmountField
                    isLoading={isLoading}
                    entityId={processId}
                    entityApiPath={processCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as { Amount: number; CurrencyCode: string }}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.CheckboxField:
            return (
                <CustomCheckbox
                    isLoading={isLoading}
                    entityId={processId}
                    entityApiPath={processCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    value={value as boolean}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.LegalEntityField:
            return (
                <CustomLegalEntityField
                    isLoading={isLoading}
                    entityId={processId}
                    entityApiPath={processCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    values={value as string[]}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.UserField:
            return (
                <CustomUserField
                    isLoading={isLoading}
                    entityId={processId}
                    entityApiPath={processCustomFieldApiPath}
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={field.customInputFieldDescription}
                    values={value as string[]}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    isPreview={isPreview}
                    errorText={errorText}
                />
            );
        case InputFieldTypeEnum.CalculatedField:
            return (
                <CustomCalculatedField
                    field={field}
                    isLoading={isLoading}
                    fieldDescription={field.customInputFieldDescription}
                    isPreview={isPreview}
                    errorText={errorText}
                    templateEntityType={TemplateEntityType.Process}
                    computedFieldEditing={computedFieldEditing}
                    entityTemplateId={process?.entityTemplateId}
                    entity={{ customFieldValues: process?.customFieldValues ?? [] }}
                />
            );

        case InputFieldTypeEnum.DatePeriodField:
            return (
                <CustomDatePeriodField
                    field={field}
                    isLoading={isLoading}
                    fieldDescription={field.customInputFieldDescription}
                    isPreview={isPreview}
                    errorText={errorText}
                    templateEntityType={TemplateEntityType.Process}
                    computedFieldEditing={computedFieldEditing}
                    entity={{ customFieldValues: process?.customFieldValues ?? [] }}
                />
            );
        default:
            return exhaustiveGuard(field.customInputFieldType);
    }
};

export const ProcessFieldRenderer: React.FC<ProcessFieldRendererProps> = (props) => {
    if (props.field.standardFieldIdentifier) {
        return (
            <ProcessStandardFieldRenderer
                field={props.field}
                isLoading={props.isLoading}
                process={props.process}
                processId={props.processId ?? ""}
                onChange={props.onChange}
                validationErrors={props.validationErrors ?? []}
                isPreview={props.isPreview}
            />
        );
    }

    return (
        <ProcessCustomFieldRenderer
            process={props.process}
            field={props.field}
            onChange={props.onCustomFieldChange}
            isPreview={props.isPreview}
            processId={props.processId ?? ""}
            isLoading={props.isLoading}
            validationErrors={props.validationErrors ?? []}
            computedFieldEditing={props.computedFieldEditing}
        />
    );
};
